import { ChatChannels } from '@core/chat';
import { PresentationSlides } from '@core/presentation-panel';
import { AuthContext } from '@core/shared';
import { ClientType, LiveStatus, VideoLayout, WebinarStatus } from '@core/types';
import { Button, Clock, Confirm, ErrorMessagePage, Loader, MobileMenuNavigatorBroadcaster, useLayout } from '@core/ui';
import { AccountWizardPage } from '@online-webinar/account';
import { useParams } from '@online-webinar/router';
import {
    defaultMoqPublisher,
    hideReconnectWarning,
    isChromiumBrowser,
    isExternalEncoder,
    isStreamValidationBlocking,
    resolveIngestType,
    setIngestType,
    joinWebinar,
    leaveWebinar,
    PresenterStatus,
    readPreferClassicConnection,
    setDeviceIds,
    setLiveStatus,
    setMoqPublisher,
    setVideoLayout,
    startWebinarCountdown,
    stopWebinar,
    useAppDispatch,
    useAppSelector
} from '@online-webinar/store';
import { lazy, Suspense, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AudioAndVideoSelection,
    BroadcastStatus,
    BroadcastViewerCount,
    ChatContent,
    ConnectionIssue,
    EventEndedInfo,
    ParticipantList,
    PollsContainer,
    RemainingTimeToEndPanel,
    StartInTime,
    TestWebinarCountdown,
    TooEarlyPopup,
    WebinarBasicInfo,
    WebinarButtonEnd,
    WebinarButtonPause,
    WebinarButtonResume,
    WebinarButtonStart,
    WebinarCancelledPopup,
    WebinarCountdownPopup,
    WebinarEndedPopup,
    WebinarErrorPopup,
    WebinarExpiredPopup,
    WebinarInfoAndControls
} from '../..';
import { useLineProbe } from '../../../hooks';
import { Popup, WebinarDuration } from '../../atoms';
import { ChaptersContainer } from '../../molecules/ChaptersContainer';
const PublishAudioAndVideo = lazy(() => import('../../organism/PublishAudioAndVideo'));
const BroadcastAudioAndVideo = lazy(() => import('../../organism/BroadcastAudioAndVideo'));
const BroadcastVideoPreview = lazy(() => import('../../organism/BroadcastVideoPreview'));

export const BroadcastLiveWebinar = () => {
    const { webinarId, linkCode } = useParams({ strict: false });

    const { t } = useTranslation();
    const {
        mainContainerRef,
        slidesActivated,
        chatActivated,
        chapterActivated,
        participantsActivated,
        pollsActivated,
        mobileMenuEnabled,
        onMobileMenuItemClicked
    } = useLayout();

    const { isAuthenticated, refreshToken } = useContext(AuthContext);
    const browserSupported = useAppSelector((state) => state.liveWebinar.browserSupported);

    // A broadcast must not be cut short by the session running out. Every refresh extends the login
    // session, the cookie and the token by their full lifetime, so one at the start of the broadcast
    // puts the next expiry a day away, and a session that is already dead shows up here, before going
    // live, instead of at the 30-minute refresh halfway through. A renewed token never remounts this
    // page; only a change of identity does.
    useEffect(() => {
        if (!isAuthenticated) return;
        void refreshToken();
    }, [isAuthenticated, refreshToken]);
    const showReconnectWarning = useAppSelector((state) => state.liveWebinar.showReconnectWarning);
    const webinarData = useAppSelector((state) => state.liveWebinar.webinarData);
    const liveStatus = useAppSelector((state) => state.liveWebinar.liveStatus);
    const dataConnection = useAppSelector((state) => state.liveWebinar.dataConnection);
    const activeSlideNumber = useAppSelector((state) => state.liveWebinar.activeSlideNumber);
    const slideDeck = useAppSelector((state) => state.liveWebinar.slideDeck);
    const videoLayout = useAppSelector((state) => state.liveWebinar.videoLayout);
    const presenterStatus = useAppSelector((state) => (state.liveWebinar.presenterStatus !== null ? state.liveWebinar.presenterStatus : PresenterStatus.Error));
    const videoDeviceId = useAppSelector((state) => state.liveWebinar.videoDeviceId);
    const moqPublisher = useAppSelector((state) => state.liveWebinar.moqPublisher);
    const isActivePresenter = useAppSelector((state) => state.liveWebinar.isActivePresenter);
    const streamValidation = useAppSelector((state) => state.liveWebinar.streamValidation);
    const broadcastState = useAppSelector((state) => state.liveWebinar.broadcastState);

    // Measures the active presenter's line in the background; shows nothing (spec 8).
    useLineProbe();

    const dispatch = useAppDispatch();

    const [showEndWebinarConfirmation, setShowEndWebinarConfirmation] = useState(false);

    const [videoMuted, setVideoMuted] = useState(false);
    const [audioMuted, setAudioMuted] = useState(false);

    // Send Join Webinar
    useEffect(() => {
        if (!isAuthenticated || !webinarId) {
            return;
        }

        // Do not send "Join Webinar" if the browser isn't supported
        if (browserSupported) {
            dispatch(joinWebinar({ webinarId: webinarId!, clientType: ClientType.presenter, authenticationCode: linkCode! }));
        }
        return () => {
            if (isAuthenticated && !!webinarId && browserSupported) {
                dispatch(setDeviceIds({ audioDeviceId: '', videoDeviceId: '' }));
                dispatch(leaveWebinar());
            }
        };
    }, [webinarId, browserSupported, isAuthenticated]);

    // Blocks only the go-live transition (WebinarButtonStart, WebinarStatus.Planned), never resume.
    // startClick is shared with WebinarButtonResume and ConnectionIssue.onResume, which run when
    // WebinarStatus is already Started; WebinarCountdownPopup then dispatches setLiveStatus(Live)
    // instead of startWebinar(), so those paths never reach the server's StartWebinarAsync gate.
    // A guard here would strand an already-live broadcast that turned blocking after go-live,
    // which the design doc explicitly forbids. So the guard lives only on the button's `disabled`.
    // Unknown is deliberately allowed to start — see the design doc.
    //
    // Gated on the source, exactly like the status panel's explanatory entry: the verdict comes
    // from Vindral's edge output, which exists for a WebRTC broadcast too, and disabling the button
    // without the reason next to it leaves the presenter with a dead control and no override.
    const isEncoderBlocked = isExternalEncoder(resolveIngestType(videoDeviceId, moqPublisher)) && isStreamValidationBlocking(streamValidation);

    // How the presenter's video reaches Vindral, reported so the encoder gate only judges a stream
    // the presenter's own encoder produced. This lives here rather than in the device dialog because
    // that dialog unmounts once devices are chosen: a presenter who takes the role over later would
    // otherwise never report, and the grain would keep the previous presenter's selection.
    //
    // Only the active presenter reports - only their video reaches the server - and the flag is in
    // the dependencies so the report still goes out when the role arrives late.
    useEffect(() => {
        if (!isActivePresenter || !videoDeviceId || moqPublisher === null) return;

        dispatch(setIngestType(resolveIngestType(videoDeviceId, moqPublisher)));
    }, [videoDeviceId, moqPublisher, isActivePresenter]);

    // MoQ is the default in Chromium unless this browser asked for the classic connection. Decided
    // once per page, on the client (the store starts at null for SSR): a fallback or a switch made on
    // this page sets a boolean and must not be overwritten.
    useEffect(() => {
        if (moqPublisher !== null) return;

        dispatch(setMoqPublisher(defaultMoqPublisher(isChromiumBrowser(), readPreferClassicConnection())));
    }, [moqPublisher]);

    const startClick = () => {
        dispatch(startWebinarCountdown());
    };

    const pauseClick = () => {
        dispatch(setLiveStatus(LiveStatus.Paused));
    };

    const endClick = () => {
        dispatch(stopWebinar());
    };

    // We cannot pause an external encoder's ingress: whatever it sends keeps reaching the channel and
    // the audience, whatever this webinar's state says. Rather than half-measures we cannot guarantee,
    // tell the only person who can actually stop it.
    //
    // Only when the programme comes off the air. Before go-live the webinar sits in Paused too, as soon as
    // the encoder pushes, and the notice then greeted every presenter on every page load.
    const [showEncoderPauseNotice, setShowEncoderPauseNotice] = useState(false);
    const previousLiveStatus = useRef(liveStatus);
    useEffect(() => {
        const wasLive = previousLiveStatus.current === LiveStatus.Live;
        previousLiveStatus.current = liveStatus;

        if (!wasLive || liveStatus !== LiveStatus.Paused) return;
        if (!isActivePresenter) return;
        if (!isExternalEncoder(resolveIngestType(videoDeviceId, moqPublisher))) return;

        setShowEncoderPauseNotice(true);
    }, [liveStatus, isActivePresenter, videoDeviceId, moqPublisher]);

    // Shown once per automatic fallback; a presenter who chose the switch needs no explanation.
    const moqFallback = useAppSelector((state) => state.liveWebinar.moqFallback);
    const [dismissedMoqFallbackAt, setDismissedMoqFallbackAt] = useState<number | null>(null);
    // Not for 'unsupported' either: nothing was attempted, and with MoQ as the default such a browser
    // would see "could not connect" at the start of every broadcast.
    const showMoqFallbackNotice =
        !!moqFallback && moqFallback.reason !== 'user_switched' && moqFallback.reason !== 'unsupported' && dismissedMoqFallbackAt !== moqFallback.at;
    const dismissMoqFallbackNotice = () => setDismissedMoqFallbackAt(moqFallback?.at ?? null);

    const onAudioVideoSelect = (audioDeviceId: string, videoDeviceId: string) => {
        dispatch(setDeviceIds({ audioDeviceId, videoDeviceId }));
    };

    const deskopSharing = () => {
        dispatch(setVideoLayout(VideoLayout.DesktopSharing));
    };

    useEffect(() => {
        document.body.classList.add('no-menu');
        return () => {
            document.body.classList.remove('no-menu');
        };
    }, []);

    if (!isAuthenticated) {
        return <AccountWizardPage isBroadcaster={true} />;
    }

    if (dataConnection.status === LiveStatus.Error) {
        const error = dataConnection.error ?? { statusCode: 500, success: false };
        if (error.statusCode === 401) {
            return <WebinarErrorPopup webinarId={webinarId!} title={t('Broadcaster.AccessDenied.Title')} message={t('Broadcaster.AccessDenied')} />;
        }
        return <ErrorMessagePage apiError={error} />;
    }

    return (
        <>
            {presenterStatus === PresenterStatus.Connecting && <Loader classNames="m-auto" />}
            {presenterStatus === PresenterStatus.Finished && webinarData.status === WebinarStatus.Ended && <WebinarEndedPopup webinarId={webinarId!} />}
            {presenterStatus === PresenterStatus.Finished && webinarData.status === WebinarStatus.Cancelled && <WebinarCancelledPopup />}
            {presenterStatus === PresenterStatus.NoBooking && (
                <WebinarErrorPopup webinarId={webinarId!} title={t('Broadcaster.NoBooking.Title')} message={t('Broadcaster.NoBooking')} />
            )}
            {presenterStatus === PresenterStatus.Finished && webinarData.status === WebinarStatus.Expired && <WebinarExpiredPopup />}
            {presenterStatus === PresenterStatus.Error && (
                <WebinarErrorPopup webinarId={webinarId!} title={t('Broadcaster.Error.Title')} message={t('Broadcaster.Error')} />
            )}
            {presenterStatus === PresenterStatus.ErrorUnsupportedBrowser && (
                <WebinarErrorPopup webinarId={webinarId!} title={t('Broadcaster.UnsupportedBrowser.Title')} message={t('Broadcaster.UnsupportedBrowser')} />
            )}
            {presenterStatus === PresenterStatus.TooEarly && <TooEarlyPopup />}
            {presenterStatus === PresenterStatus.SelectDevices && <AudioAndVideoSelection onAudioVideoSelect={onAudioVideoSelect} />}
            {presenterStatus >= PresenterStatus.WaitingForTimeslot && presenterStatus < PresenterStatus.Finished && (
                <div
                    ref={mainContainerRef}
                    className="grid grid-rows-[auto_auto_auto_1fr_auto] grid-cols-1 bc:grid-rows-[auto_auto_auto_1fr] bc:grid-cols-[1fr_clamp(300px,20vw,400px)_clamp(300px,20vw,400px)] bc:gap-x-0.5 h-dvh"
                >
                    {presenterStatus === PresenterStatus.Countdown && <WebinarCountdownPopup />}
                    {showReconnectWarning && (
                        <ConnectionIssue
                            canResume={presenterStatus === PresenterStatus.Ready}
                            canEnd={dataConnection.status === LiveStatus.Live}
                            waitingForMedia={dataConnection.status === LiveStatus.Live && broadcastState !== 'broadcasting'}
                            externalEncoder={isExternalEncoder(resolveIngestType(videoDeviceId, moqPublisher))}
                            onResume={() => {
                                dispatch(hideReconnectWarning());
                                startClick();
                            }}
                            onClose={() => {
                                dispatch(hideReconnectWarning());
                            }}
                        />
                    )}
                    <Popup
                        title={t('Broadcaster.Dialog.EncoderPause.Title')}
                        open={showEncoderPauseNotice}
                        onClose={() => setShowEncoderPauseNotice(false)}
                        onConfirm={() => setShowEncoderPauseNotice(false)}
                        isCloseFunctionality={true}
                        hasWarning={true}
                    >
                        {t('Broadcaster.Dialog.EncoderPause.Text')}
                    </Popup>
                    <Popup
                        title={t('Broadcaster.Dialog.MoqFallback.Title')}
                        open={showMoqFallbackNotice}
                        onClose={dismissMoqFallbackNotice}
                        onConfirm={dismissMoqFallbackNotice}
                        isCloseFunctionality={true}
                        hasWarning={true}
                    >
                        {t('Broadcaster.Dialog.MoqFallback.Text')}
                    </Popup>
                    <Confirm
                        title={t('Broadcaster.Dialog.EndWebinar.Title')}
                        closeText={t('Generic.Button.Cancel')}
                        confirmText={t('Broadcaster.Dialog.EndWebinar.Button.Confirm')}
                        open={showEndWebinarConfirmation}
                        onClose={() => setShowEndWebinarConfirmation(false)}
                        onConfirm={endClick}
                    >
                        {t('Broadcaster.Dialog.EndWebinar.Text')}
                    </Confirm>
                    <div className="col-span-full row-start-1">
                        <TestWebinarCountdown />
                        <RemainingTimeToEndPanel />
                    </div>
                    <div className="relative col-span-full row-start-2 bg-white flex flex-wrap justify-end bc:justify-between px-4 py-2">
                        {/* BroadcastWebinarHeader */}
                        <h1 className="sr-only">Broadcast webinar</h1>
                        <WebinarBasicInfo />
                        <WebinarInfoAndControls>
                            {webinarData.status === WebinarStatus.Planned && liveStatus !== LiveStatus.Live && liveStatus !== LiveStatus.Paused && (
                                <p className="bg-white cursor-not-allowed text-black rounded-button border-2 border-success inline-flex justify-center items-center font-medium leading-5 py-3 px-4 md:px-8">
                                    <Clock classNames="h-4 stroke-current" />
                                    <span className="sr-only bc:not-sr-only bc:ml-3 opacity-60">{t('Broadcaster.Button.Start')}</span>
                                </p>
                            )}
                            {presenterStatus === PresenterStatus.Ready &&
                                webinarData.status === WebinarStatus.Planned &&
                                dataConnection.status === LiveStatus.Live &&
                                (liveStatus === LiveStatus.Live || liveStatus === LiveStatus.Paused) && (
                                    <WebinarButtonStart onClick={startClick} disabled={isEncoderBlocked} />
                                )}
                            {presenterStatus === PresenterStatus.Ready &&
                                webinarData.status === WebinarStatus.Started &&
                                dataConnection.status === LiveStatus.Live &&
                                (liveStatus === LiveStatus.Live || liveStatus === LiveStatus.Paused) && <WebinarButtonResume onClick={startClick} />}
                            {presenterStatus === PresenterStatus.Live && dataConnection.status === LiveStatus.Live && (
                                <WebinarButtonPause onClick={pauseClick} />
                            )}
                            {/* You should always be able to end a webinar that has been started, regardless of other states */}
                            {webinarData.status === WebinarStatus.Started && dataConnection.status === LiveStatus.Live && (
                                <WebinarButtonEnd onClick={() => setShowEndWebinarConfirmation(true)} />
                            )}
                            {(liveStatus === LiveStatus.Live || liveStatus === LiveStatus.Paused) && videoDeviceId !== 'rtmp' && videoDeviceId !== 'srt' && (
                                <Button onClick={deskopSharing} variant="primary-outline">
                                    {t('Broadcaster.Button.DesktopShare')}
                                </Button>
                            )}

                            <div className="flex leading-none broadcast-header-actions">
                                {webinarData.status === WebinarStatus.Planned && <StartInTime />}
                                {webinarData.status === WebinarStatus.Started && <WebinarDuration />}
                                {webinarData.status === WebinarStatus.Ended && <WebinarDuration />}
                                <div className="vertical-seperator"></div>
                                <BroadcastViewerCount />
                                <div className="vertical-seperator"></div>
                                <BroadcastStatus />
                            </div>
                        </WebinarInfoAndControls>
                    </div>
                    <div
                        className={`relative col-start-1 row-start-3 ${videoLayout === VideoLayout.Classic ? 'bc:max-h-[calc(50dvh-2rem)]' : ''} min-w-0 min-h-0 flex items-start`}
                    >
                        <div
                            className={`relative w-full max-bc:h-full max-h-96 bc:max-h-full max-bc:bg-black ${videoLayout === VideoLayout.PresentationWithVideoSmall ? 'bc:w-1/4 bc:ml-auto bc:mt-auto bc:mb-4 bc:mr-4 bc:z-1' : 'flex'}`}
                        >
                            {videoDeviceId !== 'rtmp' && videoDeviceId !== 'srt' ? (
                                <Suspense fallback={<Loader classNames="m-auto" />}>
                                    {/* Nothing until the default is decided, so WebRTC never starts first and is then swapped for MoQ. */}
                                    {moqPublisher === null ? null : moqPublisher ? (
                                        <PublishAudioAndVideo
                                            audioMuted={presenterStatus !== PresenterStatus.Live || audioMuted}
                                            videoMuted={presenterStatus !== PresenterStatus.Live || videoMuted}
                                        />
                                    ) : (
                                        <BroadcastAudioAndVideo
                                            audioMuted={presenterStatus !== PresenterStatus.Live || audioMuted}
                                            videoMuted={presenterStatus !== PresenterStatus.Live || videoMuted}
                                        />
                                    )}
                                </Suspense>
                            ) : (
                                <Suspense fallback={<Loader classNames="m-auto" />}>
                                    <BroadcastVideoPreview />
                                </Suspense>
                            )}
                        </div>
                        {webinarData.status === WebinarStatus.Ended && <EventEndedInfo />}
                    </div>

                    {(!mobileMenuEnabled || slidesActivated) && slideDeck.slides?.length !== 0 && (
                        <div
                            className={`col-start-1 row-start-4 ${videoLayout === VideoLayout.Classic ? 'bc:row-start-4' : 'bc:row-start-3'} bg-gray-100 min-w-0 min-h-0`}
                        >
                            <PresentationSlides
                                slides={slideDeck?.slides ?? []}
                                currentSlideNumber={activeSlideNumber ?? 0}
                                totalSlides={slideDeck?.slides?.length ?? 0}
                                classNames="max-bc:max-h-[calc(50vh-80px)]"
                                showFullScreen="hidden"
                                videoLayout={videoLayout}
                                isBroadcaster
                            />
                        </div>
                    )}
                    <div className="col-start-1 row-start-4 bc:col-start-2 bc:row-start-3 bc:row-end-5 grid bc:grid-rows-[auto_1fr] bc:gap-0.5 min-h-0">
                        {(!mobileMenuEnabled || chapterActivated) && <ChaptersContainer />}
                        {(!mobileMenuEnabled || chatActivated) && (
                            <ChatContent>
                                <ChatChannels />
                            </ChatContent>
                        )}
                    </div>
                    <div className="col-start-1 row-start-4 bc:col-start-3 bc:row-start-3 bc:row-end-5 grid bc:grid-rows-2 bc:gap-0.5 min-h-0">
                        {(!mobileMenuEnabled || pollsActivated) && <PollsContainer />}
                        {(!mobileMenuEnabled || participantsActivated) && (
                            <div className="flex">
                                <ParticipantList />
                            </div>
                        )}
                    </div>
                    {mobileMenuEnabled && (
                        <div className="col-start-1 row-start-5">
                            <MobileMenuNavigatorBroadcaster onItemClick={onMobileMenuItemClicked} />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};
